"use client";

import React, { useState } from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, ChevronRight, Layers, ShieldCheck, Table as TableIcon, LayoutGrid, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface AlloyGrade {
  grade: string;
  name: Record<Lang, string>;
  unsNumber: string;
  composition: string;
  corrosionRating: Record<Lang, string>;
  magnetism: Record<Lang, string>;
  keyProperties: Record<Lang, string>;
  typicalUses: Record<Lang, string>;
  acidResistance: Record<Lang, string>;
  chlorideTolerance: Record<Lang, string>;
}
const ALLOY_GRADES: AlloyGrade[] = [{
  grade: "304",
  name: {
    en: "Standard Austenitic Stainless",
    zh: "标准奥氏体不锈钢",
    es: "Acero inoxidable austenítico estándar",
    de: "Austenitischer Standard-Edelstahl",
    fr: "Acier inoxydable austénitique standard",
    pt: "Aço inoxidável austenítico padrão"
  },
  unsNumber: "UNS S30400",
  composition: "18% Cr, 8% Ni",
  corrosionRating: {
    en: "Good general atmospheric & liquid resistance",
    zh: "良好的通用大气与常规液体介质耐腐蚀性",
    es: "Buena resistencia general atmosférica y a líquidos",
    de: "Gute Beständigkeit gegen Atmosphäre und Flüssigkeiten",
    fr: "Bonne résistance générale atmosphérique et aux liquides",
    pt: "Boa resistência geral atmosférica e a líquidos"
  },
  magnetism: {
    en: "Non-magnetic (mildly magnetic when cold worked)",
    zh: "无磁性（冷加工后具有微弱磁性）",
    es: "No magnético (ligeramente magnético tras conformado en frío)",
    de: "Nicht magnetisch (leicht magnetisch nach Kaltverformung)",
    fr: "Non magnétique (légèrement magnétique après travail à froid)",
    pt: "Não magnético (levemente magnético quando trabalhado a frio)"
  },
  keyProperties: {
    en: "High mechanical strength, excellent formability, cost-effective baseline.",
    zh: "高机械强度，优异的冷弯成形性，高性价比的基础选型标准。",
    es: "Alta resistencia mecánica, excelente conformabilidad, referencia económica.",
    de: "Hohe mechanische Festigkeit, hervorragende Umformbarkeit, wirtschaftliche Basis.",
    fr: "Haute résistance mécanique, excellente formabilité, référence économique.",
    pt: "Alta resistência mecânica, excelente conformabilidade, referência econômica."
  },
  typicalUses: {
    en: "Standard screening, air intake filters, general water filtration, architectural mesh.",
    zh: "常规固液筛分、进气粗滤网、工业水处理过滤、建筑装饰丝网。",
    es: "Tamizado estándar, filtros de toma de aire, filtración de agua general, malla arquitectónica.",
    de: "Standard-Siebung, Lufteinlassfilter, allgemeine Wasserfiltration, Architektur-Gewebe.",
    fr: "Criblage standard, filtres d'admission d'air, filtration d'eau générale, toiles architecturales.",
    pt: "Peneiramento padrão, filtros de admissão de ar, filtragem geral de água, telas arquitetônicas."
  },
  acidResistance: {
    en: "Moderate (organic acids)",
    zh: "中等（耐受弱有机酸）",
    es: "Moderada (ácidos orgánicos)",
    de: "Mäßig (organische Säuren)",
    fr: "Modérée (acides organiques)",
    pt: "Moderada (ácidos orgânicos)"
  },
  chlorideTolerance: {
    en: "Low (risk of pitting >100 ppm)",
    zh: "较低（氯离子浓度 >100 ppm 存在点蚀风险）",
    es: "Baja (riesgo de picaduras >100 ppm)",
    de: "Gering (Lochfraßrisiko >100 ppm)",
    fr: "Faible (risque de piqûres >100 ppm)",
    pt: "Baixa (risco de corrosão por pite >100 ppm)"
  }
}, {
  grade: "304L",
  name: {
    en: "Low Carbon Austenitic Stainless",
    zh: "超低碳奥氏体不锈钢",
    es: "Acero inoxidable austenítico bajo en carbono",
    de: "Kohlenstoffarmer austenitischer Edelstahl",
    fr: "Acier inoxydable austénitique bas carbone",
    pt: "Aço inoxidável austenítico de baixo carbono"
  },
  unsNumber: "UNS S30403",
  composition: "18% Cr, 8% Ni (C ≤ 0.03%)",
  corrosionRating: {
    en: "Resistant to intergranular corrosion after welding",
    zh: "焊接后具备优异的抗晶间腐蚀能力",
    es: "Resistente a la corrosión intergranular tras soldadura",
    de: "Beständig gegen interkristalline Korrosion nach dem Schweißen",
    fr: "Résistant à la corrosion intergranulaire après soudage",
    pt: "Resistente à corrosão intergranular após soldagem"
  },
  magnetism: {
    en: "Non-magnetic",
    zh: "无磁性",
    es: "No magnético",
    de: "Nicht magnetisch",
    fr: "Non magnétique",
    pt: "Não magnético"
  },
  keyProperties: {
    en: "Eliminates carbide precipitation in heat-affected zones during seam welding.",
    zh: "有效抑制缝焊与点焊热影响区的晶界碳化物析出。",
    es: "Elimina la precipitación de carburos en zonas afectadas por el calor durante soldadura.",
    de: "Verhindert Carbidausscheidung in Wärmeeinflusszonen beim Nahtschweißen.",
    fr: "Élimine la précipitation de carbures dans les zones thermiquement affectées au soudage.",
    pt: "Elimina a precipitação de carbonetos nas zonas termicamente afetadas durante a soldagem."
  },
  typicalUses: {
    en: "Welded filter cylinders, joined mesh baskets, food processing, sanitary housings.",
    zh: "焊接滤筒、拼接滤篮、食品卫生加工装备、洁净级过滤器外壳。",
    es: "Cilindros filtrantes soldados, cestas unidas, procesado de alimentos, carcasas sanitarias.",
    de: "Geschweißte Filterzylinder, Filterkörbe, Lebensmittelverarbeitung, Sanitärgehäuse.",
    fr: "Cylindres filtrants soudés, paniers assemblés, agroalimentaire, carters sanitaires.",
    pt: "Cilindros de filtro soldados, cestos emendados, processamento de alimentos, carcaças sanitárias."
  },
  acidResistance: {
    en: "Moderate (welded assemblies)",
    zh: "中等（适用于焊接组装件）",
    es: "Moderada (conjuntos soldados)",
    de: "Mäßig (geschweißte Baugruppen)",
    fr: "Modérée (ensembles soudés)",
    pt: "Moderada (conjuntos soldados)"
  },
  chlorideTolerance: {
    en: "Low (similar to 304)",
    zh: "较低（与 304 相当）",
    es: "Baja (similar a 304)",
    de: "Gering (ähnlich wie 304)",
    fr: "Faible (similaire au 304)",
    pt: "Baixa (similar ao 304)"
  }
}, {
  grade: "316",
  name: {
    en: "Molybdenum-Enhanced Stainless",
    zh: "含钼增强型耐蚀不锈钢",
    es: "Acero inoxidable reforzado con molibdeno",
    de: "Molybdän-legierter Edelstahl",
    fr: "Acier inoxydable enrichi en molybdène",
    pt: "Aço inoxidável enriquecido com molibdênio"
  },
  unsNumber: "UNS S31600",
  composition: "16% Cr, 10% Ni, 2% Mo",
  corrosionRating: {
    en: "High resistance to chlorides and industrial chemicals",
    zh: "对氯离子与工业化学介质具备优良的抗腐蚀性能",
    es: "Alta resistencia a cloruros y químicos industriales",
    de: "Hohe Beständigkeit gegen Chloride und Industriechemikalien",
    fr: "Haute résistance aux chlorures et produits chimiques industriels",
    pt: "Alta resistência a cloretos e produtos químicos industriais"
  },
  magnetism: {
    en: "Non-magnetic",
    zh: "无磁性",
    es: "No magnético",
    de: "Nicht magnetisch",
    fr: "Non magnétique",
    pt: "Não magnético"
  },
  keyProperties: {
    en: "2-3% Molybdenum addition provides robust defense against pitting and crevice corrosion.",
    zh: "添加 2-3% 钼元素，显著增强耐点蚀和抗缝隙腐蚀性能。",
    es: "La adición de 2-3% de molibdeno ofrece sólida defensa contra corrosión por picaduras y rendijas.",
    de: "2-3% Molybdänzusatz bietet robusten Schutz gegen Lochfraß und Spaltkorrosion.",
    fr: "L'ajout de 2-3% de molybdène procure une défense robuste contre piqûres et corrosion caverneuse.",
    pt: "A adição de 2-3% de molibdênio oferece defesa robusta contra corrosão por pite e em frestas."
  },
  typicalUses: {
    en: "Chemical processing mesh, marine filtration, pharmaceuticals, fuel and oil filters.",
    zh: "化工工艺滤网、海洋工程过滤、制药提纯过滤、航空燃油与润滑油精滤。",
    es: "Malla para procesos químicos, filtración marina, farmacéutica, filtros de aceite y combustible.",
    de: "Chemische Prozessfiltration, Meerwasserfiltration, Pharma, Kraftstoff- und Ölfilter.",
    fr: "Toiles pour procédés chimiques, filtration marine, pharmacie, filtres carburant et huile.",
    pt: "Telas de processo químico, filtragem marítima, produtos farmacêuticos, filtros de óleo e combustível."
  },
  acidResistance: {
    en: "High (sulfuric, acetic, tartaric)",
    zh: "优良（耐受硫酸、醋酸、酒石酸等介质）",
    es: "Alta (sulfúrico, acético, tartárico)",
    de: "Hoch (Schwefelsäure, Essigsäure, Weinsäure)",
    fr: "Haute (sulfurique, acétique, tartrique)",
    pt: "Alta (sulfúrico, acético, tartárico)"
  },
  chlorideTolerance: {
    en: "Moderate (up to 1,000 ppm)",
    zh: "中等（耐受最高约 1,000 ppm 氯离子）",
    es: "Moderada (hasta 1.000 ppm)",
    de: "Mäßig (bis zu 1.000 ppm)",
    fr: "Modérée (jusqu'à 1 000 ppm)",
    pt: "Moderada (até 1.000 ppm)"
  }
}, {
  grade: "316L",
  name: {
    en: "Low Carbon Marine / Pharma Grade",
    zh: "低碳海洋与高纯医药级不锈钢",
    es: "Grado marino / farmacéutico bajo en carbono",
    de: "Kohlenstoffarme Marine- / Pharma-Güte",
    fr: "Nuance marine / pharmaceutique bas carbone",
    pt: "Grau marítimo / farmacêutico de baixo carbono"
  },
  unsNumber: "UNS S31603",
  composition: "16% Cr, 10% Ni, 2% Mo (C ≤ 0.03%)",
  corrosionRating: {
    en: "Superior corrosion resistance in welded chemical environments",
    zh: "焊接化工工况中表现出卓越的综合耐腐蚀性",
    es: "Resistencia superior a la corrosión en entornos químicos soldados",
    de: "Hervorragende Korrosionsbeständigkeit in geschweißten chemischen Umgebungen",
    fr: "Résistance supérieure à la corrosion en milieux chimiques soudés",
    pt: "Resistência superior à corrosão em ambientes químicos soldados"
  },
  magnetism: {
    en: "Non-magnetic",
    zh: "无磁性",
    es: "No magnético",
    de: "Nicht magnetisch",
    fr: "Non magnétique",
    pt: "Não magnético"
  },
  keyProperties: {
    en: "Combines 316 pitting resistance with low carbon for welded filter packs and discs.",
    zh: "结合 316 耐点蚀性能与低碳特性，专用于焊接多层滤片与组合滤网。",
    es: "Combina la resistencia a picaduras del 316 con bajo carbono para paquetes y discos soldados.",
    de: "Kombiniert 316-Lochfraßbeständigkeit mit niedrigem Kohlenstoff für geschweißte Filterpakete.",
    fr: "Combine la résistance aux piqûres du 316 avec un bas carbone pour filtres et disques soudés.",
    pt: "Combina a resistência a pites do 316 com baixo carbono para discos e pacotes filtrantes soldados."
  },
  typicalUses: {
    en: "Sintered multi-layer mesh, pharmaceutical filter cloth, high-purity bioprocess screens.",
    zh: "多层金属烧结网、医药精细滤布、高纯生物制药工艺筛网与滤芯。",
    es: "Malla sinterizada multicapa, tela filtrante farmacéutica, tamices de bioprocesos de alta pureza.",
    de: "Mehrschichtiges Sintergewebe, pharmazeutische Filtertücher, Reinst-Bioprozesssiebe.",
    fr: "Toiles frittées multicouches, toiles filtrantes pharmaceutiques, tamis bioprocédés haute pureté.",
    pt: "Telas sinterizadas multicamadas, tecido filtrante farmacêutico, telas de bioprocesso de alta pureza."
  },
  acidResistance: {
    en: "High (welded process vessels)",
    zh: "优良（适用于焊接工艺承压容器）",
    es: "Alta (recipientes de proceso soldados)",
    de: "Hoch (geschweißte Prozessbehälter)",
    fr: "Haute (cuves de procédé soudées)",
    pt: "Alta (vasos de processo soldados)"
  },
  chlorideTolerance: {
    en: "Moderate (up to 1,000 ppm)",
    zh: "中等（耐受最高约 1,000 ppm 氯离子）",
    es: "Moderada (hasta 1.000 ppm)",
    de: "Mäßig (bis zu 1.000 ppm)",
    fr: "Modérée (jusqu'à 1 000 ppm)",
    pt: "Moderada (até 1.000 ppm)"
  }
}];
interface SectionCopy {
  eyebrow: string;
  heading: string;
  lead: string;
  cardsView: string;
  tableView: string;
  allMetallurgy: string;
  mostSpecifiedBadge: string;
  nominalChemistry: string;
  performanceProfile: string;
  typicalApplications: string;
  chlorideRating: string;
  gradeSpecLink: string;
  thGradeUns: string;
  thNominalChemistry: string;
  thCorrosionResistance: string;
  thAcidChloride: string;
  thPrimaryFilterApps: string;
  tableAcidLabel: string;
  tableChlorideLabel: string;
  tableStandardBadge: string;
  bannerTitle: string;
  bannerDesc: string;
  btnCorrosionMatrices: string;
  btnSelectionGuide: string;
}
const copyData: Record<Lang, SectionCopy> = {
  en: {
    eyebrow: "Alloy Matrix",
    heading: "Stainless steel grades for industrial wire mesh",
    lead: "Compare chemical composition, temperature tolerance, and corrosion resistance to select the right wire alloy for your filtration process.",
    cardsView: "Cards",
    tableView: "Table",
    allMetallurgy: "All metallurgy",
    mostSpecifiedBadge: "Most Specified",
    nominalChemistry: "Nominal chemistry",
    performanceProfile: "Performance profile",
    typicalApplications: "Typical applications",
    chlorideRating: "Chloride rating: ",
    gradeSpecLink: "Grade spec",
    thGradeUns: "Grade / UNS",
    thNominalChemistry: "Nominal Chemistry",
    thCorrosionResistance: "Corrosion Resistance",
    thAcidChloride: "Acid & Chloride Tolerance",
    thPrimaryFilterApps: "Primary Filter Applications",
    tableAcidLabel: "Acids: ",
    tableChlorideLabel: "Chlorides: ",
    tableStandardBadge: "Standard",
    bannerTitle: "Verify material compatibility before placing production orders",
    bannerDesc: "Consult our chemical compatibility matrices and alloy selection guides to evaluate localized pitting, stress corrosion cracking, or thermal degradation for critical filtration processes.",
    btnCorrosionMatrices: "Corrosion matrices",
    btnSelectionGuide: "Material selection guide"
  },
  zh: {
    eyebrow: "合金选型矩阵",
    heading: "工业编织丝网不锈钢材质牌号",
    lead: "对比化学成分、耐温极限和耐腐蚀等级，为您的过滤工艺精准选配合金材质。",
    cardsView: "卡片视图",
    tableView: "表格视图",
    allMetallurgy: "全部冶金材质",
    mostSpecifiedBadge: "标准应用牌号",
    nominalChemistry: "公称化学成分",
    performanceProfile: "性能特征",
    typicalApplications: "典型应用领域",
    chlorideRating: "耐氯离子等级: ",
    gradeSpecLink: "牌号详情",
    thGradeUns: "牌号 / UNS编号",
    thNominalChemistry: "公称化学成分",
    thCorrosionResistance: "耐腐蚀性能",
    thAcidChloride: "耐酸与氯离子耐受性",
    thPrimaryFilterApps: "主要过滤应用",
    tableAcidLabel: "耐酸性: ",
    tableChlorideLabel: "耐氯离子: ",
    tableStandardBadge: "标准应用",
    bannerTitle: "在批量生产前验证材料耐腐蚀与介质兼容性",
    bannerDesc: "参考我们的化学兼容性矩阵与合金选型指南，预防关键过滤系统中的局部点蚀、应力腐蚀开裂或高温环境失效。",
    btnCorrosionMatrices: "耐腐蚀矩阵",
    btnSelectionGuide: "材料选型指南"
  },
  es: {
    eyebrow: "Matriz de aleaciones",
    heading: "Grados de acero inoxidable para malla metálica industrial",
    lead: "Compare la composición química, la tolerancia térmica y la resistencia a la corrosión para seleccionar la aleación adecuada para su proceso de filtración.",
    cardsView: "Tarjetas",
    tableView: "Tabla",
    allMetallurgy: "Toda la metalurgia",
    mostSpecifiedBadge: "Más especificado",
    nominalChemistry: "Composición nominal",
    performanceProfile: "Perfil de rendimiento",
    typicalApplications: "Aplicaciones típicas",
    chlorideRating: "Clasificación de cloruros: ",
    gradeSpecLink: "Detalle de grado",
    thGradeUns: "Grado / UNS",
    thNominalChemistry: "Composición nominal",
    thCorrosionResistance: "Resistencia a corrosión",
    thAcidChloride: "Tolerancia a ácidos y cloruros",
    thPrimaryFilterApps: "Aplicaciones de filtración primarias",
    tableAcidLabel: "Ácidos: ",
    tableChlorideLabel: "Cloruros: ",
    tableStandardBadge: "Estándar",
    bannerTitle: "Verifique la compatibilidad del material antes de órdenes de producción",
    bannerDesc: "Consulte nuestras matrices de compatibilidad química y guías de selección de aleaciones para evaluar corrosión por picaduras, agrietamiento por tensión o degradación térmica.",
    btnCorrosionMatrices: "Matrices de corrosión",
    btnSelectionGuide: "Guía de selección de materiales"
  },
  de: {
    eyebrow: "Legierungsmatrix",
    heading: "Edelstahlsorten für industrielle Drahtgewebe",
    lead: "Vergleichen Sie chemische Zusammensetzung, Temperaturbeständigkeit und Korrosionsverhalten zur Auswahl der passenden Legierung für Ihre Filtration.",
    cardsView: "Karten",
    tableView: "Tabelle",
    allMetallurgy: "Alle Werkstoffe",
    mostSpecifiedBadge: "Meist spezifiziert",
    nominalChemistry: "Chemische Zusammensetzung",
    performanceProfile: "Leistungsprofil",
    typicalApplications: "Typische Anwendungen",
    chlorideRating: "Chloridbeständigkeit: ",
    gradeSpecLink: "Sortendetails",
    thGradeUns: "Sorte / UNS",
    thNominalChemistry: "Zusammensetzung",
    thCorrosionResistance: "Korrosionsbeständigkeit",
    thAcidChloride: "Säure- & Chloridbeständigkeit",
    thPrimaryFilterApps: "Haupt-Filteranwendungen",
    tableAcidLabel: "Säuren: ",
    tableChlorideLabel: "Chloride: ",
    tableStandardBadge: "Standard",
    bannerTitle: "Werkstoffverträglichkeit vor Serienfertigung prüfen",
    bannerDesc: "Nutzen Sie unsere chemischen Beständigkeitsmatrizen und Werkstoffleitfäden zur Vermeidung von Lochfraß, Spannungsrisskorrosion oder thermischem Versagen.",
    btnCorrosionMatrices: "Korrosionsmatrizen",
    btnSelectionGuide: "Werkstoff-Leitfaden"
  },
  fr: {
    eyebrow: "Matrice des alliages",
    heading: "Nuances d'acier inoxydable pour toiles métalliques industrielles",
    lead: "Comparez composition chimique, tolérance en température et résistance à la corrosion pour choisir l'alliage adapté à votre procédé de filtration.",
    cardsView: "Cartes",
    tableView: "Tableau",
    allMetallurgy: "Tous les matériaux",
    mostSpecifiedBadge: "Plus spécifié",
    nominalChemistry: "Composition nominale",
    performanceProfile: "Profil de performance",
    typicalApplications: "Applications types",
    chlorideRating: "Tolérance aux chlorures : ",
    gradeSpecLink: "Fiche nuance",
    thGradeUns: "Nuance / UNS",
    thNominalChemistry: "Composition nominale",
    thCorrosionResistance: "Résistance à la corrosion",
    thAcidChloride: "Tolérance acides & chlorures",
    thPrimaryFilterApps: "Applications principales de filtration",
    tableAcidLabel: "Acides : ",
    tableChlorideLabel: "Chlorures : ",
    tableStandardBadge: "Standard",
    bannerTitle: "Validez la compatibilité matière avant commande de production",
    bannerDesc: "Consultez nos matrices de compatibilité chimique et guides de sélection des alliages pour évaluer piqûres, corrosion sous contrainte ou dégradation thermique.",
    btnCorrosionMatrices: "Matrices de corrosion",
    btnSelectionGuide: "Guide de sélection matière"
  },
  pt: {
    eyebrow: "Matriz de ligas",
    heading: "Graus de aço inoxidável para telas metálicas industriais",
    lead: "Compare composição química, tolerância térmica e resistência à corrosão para selecionar a liga de arame adequada ao seu processo de filtragem.",
    cardsView: "Cartões",
    tableView: "Tabela",
    allMetallurgy: "Toda metalurgia",
    mostSpecifiedBadge: "Mais especificado",
    nominalChemistry: "Composição nominal",
    performanceProfile: "Perfil de desempenho",
    typicalApplications: "Aplicações típicas",
    chlorideRating: "Classificação de cloretos: ",
    gradeSpecLink: "Detalhes do grau",
    thGradeUns: "Grau / UNS",
    thNominalChemistry: "Composição nominal",
    thCorrosionResistance: "Resistência à corrosão",
    thAcidChloride: "Tolerância a ácidos e cloretos",
    thPrimaryFilterApps: "Principais aplicações em filtros",
    tableAcidLabel: "Ácidos: ",
    tableChlorideLabel: "Cloretos: ",
    tableStandardBadge: "Padrão",
    bannerTitle: "Valide a compatibilidade do material antes de pedidos de produção",
    bannerDesc: "Consulte nossas matrizes de compatibilidade química e guias de seleção de ligas para avaliar corrosão por pite, sob tensão ou degradação térmica.",
    btnCorrosionMatrices: "Matrizes de corrosão",
    btnSelectionGuide: "Guia de seleção de materiais"
  }
};
export default function MaterialsGrid() {
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="materialsoverviewgrid" data-nav="light" className="bg-background mf-section border-b border-border text-foreground">
      <div className="mf-container">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10 border-b border-border">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-3 px-2.5 py-1 font-semibold uppercase tracking-wider text-foreground border-border bg-muted mf-caption">
              {copy.eyebrow}
            </Badge>
            <h2 className="mf-h2 font-semibold tracking-tight text-foreground text-balance">
              {copy.heading}
            </h2>
            <p className="mt-3 mf-lead text-muted-foreground">
              {copy.lead}
            </p>
          </div>

          {/* View Mode Toggle and Resource Quicklinks */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center rounded-lg border border-border bg-card p-1">
              <Button variant={viewMode === "grid" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("grid")} className="gap-1.5 mf-caption">
                <LayoutGrid className="size-3.5" aria-hidden="true" />
                {copy.cardsView}
              </Button>
              <Button variant={viewMode === "table" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("table")} className="gap-1.5 mf-caption">
                <TableIcon className="size-3.5" aria-hidden="true" />
                {copy.tableView}
              </Button>
            </div>

            <Button variant="outline" size="sm" asChild className="mf-caption font-medium">
              <Link to="/MaterialsAndGradesPage">
                <span>{copy.allMetallurgy}</span>
                <ChevronRight className="size-3.5 ml-1" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Content Area */}
        <div className="mt-10">
          {viewMode === "grid" ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              {ALLOY_GRADES.map((item, i) => <Reveal key={item.grade} delay={Math.min(i, 3) * 100}>
                  <Card className="h-full flex flex-col justify-between border-border bg-card rounded-lg">
                    <CardHeader className="space-y-2 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <span className="text-2xl font-bold font-mono text-primary">
                            AISI {item.grade}
                          </span>
                          <span className="mf-caption font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded border border-border">
                            {item.unsNumber}
                          </span>
                        </div>
                        {item.grade === "316L" && <Badge variant="outline" className="mf-caption bg-muted text-foreground border-border">
                            {copy.mostSpecifiedBadge}
                          </Badge>}
                      </div>
                      <CardDescription className="mf-caption font-medium text-foreground">
                        {pick(lang, item.name)}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4 mf-caption flex-1">
                      {/* Specs pills */}
                      <div className="bg-muted p-3 rounded-lg border border-border mf-caption">
                        <div>
                          <span className="text-muted-foreground block mf-caption uppercase tracking-wider">
                            {copy.nominalChemistry}
                          </span>
                          <span className="font-mono font-medium text-foreground">{item.composition}</span>
                        </div>
                      </div>

                      {/* Properties & Use Cases */}
                      <div className="space-y-2.5 pt-1">
                        <div>
                          <div className="flex items-center gap-1.5 font-semibold mf-caption text-foreground uppercase tracking-wider mb-1">
                            <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                            {copy.performanceProfile}
                          </div>
                          <p className="mf-caption text-muted-foreground leading-normal">
                            {pick(lang, item.keyProperties)}
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 font-semibold mf-caption text-foreground uppercase tracking-wider mb-1">
                            <Layers className="size-3.5 text-primary" aria-hidden="true" />
                            {copy.typicalApplications}
                          </div>
                          <p className="mf-caption text-muted-foreground leading-normal">
                            {pick(lang, item.typicalUses)}
                          </p>
                        </div>
                      </div>
                    </CardContent>

                    <CardFooter className="pt-3 border-t border-border flex items-center justify-between mf-caption">
                      <div className="text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {copy.chlorideRating}
                        </span>
                        <span>{pick(lang, item.chlorideTolerance)}</span>
                      </div>
                      <Link to="/MaterialsAndGradesPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                        <span>{copy.gradeSpecLink}</span>
                        <ArrowRight className="size-3.5" aria-hidden="true" />
                      </Link>
                    </CardFooter>
                  </Card>
                </Reveal>)}
            </div> : (/* Table View */
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="w-[120px] font-bold text-foreground">
                      {copy.thGradeUns}
                    </TableHead>
                    <TableHead className="w-[160px] font-bold text-foreground">
                      {copy.thNominalChemistry}
                    </TableHead>
                    <TableHead className="font-bold text-foreground">
                      {copy.thCorrosionResistance}
                    </TableHead>
                    <TableHead className="font-bold text-foreground">
                      {copy.thAcidChloride}
                    </TableHead>
                    <TableHead className="w-[220px] font-bold text-foreground">
                      {copy.thPrimaryFilterApps}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ALLOY_GRADES.map((item, i) => <TableRow key={item.grade} data-index={i} className="hover:bg-muted/50">
                      <TableCell className="align-top py-4">
                        <div className="font-mono font-bold text-base text-primary">
                          {item.grade}
                        </div>
                        <div className="mf-caption font-mono text-muted-foreground">
                          {item.unsNumber}
                        </div>
                        {item.grade === "316L" && <Badge variant="outline" className="mt-1 mf-caption px-1.5 py-0 bg-muted text-foreground border-border">
                            {copy.tableStandardBadge}
                          </Badge>}
                      </TableCell>
                      <TableCell className="align-top py-4 mf-caption font-mono text-foreground whitespace-normal">
                        {item.composition}
                      </TableCell>
                      <TableCell className="align-top py-4 mf-caption text-muted-foreground whitespace-normal">
                        <strong className="text-foreground block font-medium mb-0.5">
                          {pick(lang, item.name)}
                        </strong>
                        {pick(lang, item.corrosionRating)}
                      </TableCell>
                      <TableCell className="align-top py-4 mf-caption whitespace-normal">
                        <div className="space-y-1">
                          <div>
                            <span className="font-medium text-foreground">
                              {copy.tableAcidLabel}
                            </span>
                            <span className="text-muted-foreground">
                              {pick(lang, item.acidResistance)}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-foreground">
                              {copy.tableChlorideLabel}
                            </span>
                            <span className="text-muted-foreground">
                              {pick(lang, item.chlorideTolerance)}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="align-top py-4 mf-caption text-muted-foreground whitespace-normal">
                        {pick(lang, item.typicalUses)}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>)}
        </div>

        {/* Bottom Technical Guides Navigation Banner */}
        <div className="mt-12 rounded-lg border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0 mt-0.5">
              <ShieldCheck className="size-5" aria-hidden="true" />
            </span>
            <div>
              <h3 className="mf-h3 font-semibold text-foreground">
                {copy.bannerTitle}
              </h3>
              <p className="mt-1 mf-caption text-muted-foreground max-w-2xl">
                {copy.bannerDesc}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0 w-full md:w-auto">
            <Button variant="outline" size="sm" asChild className="flex-1 md:flex-initial mf-caption">
              <Link to="/CorrosionResistanceMatricesPage">
                <FileText className="size-4 mr-1.5" aria-hidden="true" />
                <span>{copy.btnCorrosionMatrices}</span>
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild className="flex-1 md:flex-initial mf-caption">
              <Link to="/MaterialsAndGradesPage">
                <FileText className="size-4 mr-1.5" aria-hidden="true" />
                <span>{copy.btnSelectionGuide}</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}